import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, CircleNotch } from "@phosphor-icons/react";
import { postBooking } from "@/lib/api";

const TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM"];
const TOPICS = ["Life Insurance", "Health Insurance", "Medicare", "General review"];

const todayStr = () => new Date().toISOString().split("T")[0];

export default function Booking() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", date: "", time: "", topic: "Life Insurance", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) return toast.error("Please choose a date and time.");
    if (!form.consent) return toast.error("Please accept the consent to continue.");
    setLoading(true);
    try {
      await postBooking(form);
      toast.success("Consultation requested — we'll confirm your slot by email.");
      setForm({ name: "", email: "", phone: "", date: "", time: "", topic: "Life Insurance", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="bg-slate-navy grain relative py-24 lg:py-32">
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <p className="eyebrow text-gold mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-gold" /> Book a Consultation
          </p>
          <h2 className="font-heading font-extrabold tracking-tighter text-warm-white text-[clamp(2rem,4.5vw,3.4rem)] leading-[1]">
            Twenty focused minutes. Zero pressure.
          </h2>
          <p className="mt-5 text-light-slate leading-relaxed">
            Pick a time that works and a licensed agent will call to review your options,
            answer questions, and — only if you're ready — help you apply.
          </p>
          <div className="mt-8 flex items-center gap-3 text-warm-white">
            <CalendarCheck weight="duotone" size={28} className="text-gold" />
            <span className="text-sm text-light-slate">Free · By phone or video · Same-week availability</span>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="lg:col-span-8 rounded-2xl bg-warm-white p-7 sm:p-9"
        >
          <form onSubmit={submit} data-testid="booking-form" className="grid sm:grid-cols-2 gap-4">
            <Input label="Full name" testid="booking-name" value={form.name} onChange={(v) => set("name", v)} required />
            <Input label="Phone" type="tel" testid="booking-phone" value={form.phone} onChange={(v) => set("phone", v)} required />
            <Input label="Email" type="email" testid="booking-email" value={form.email} onChange={(v) => set("email", v)} required className="sm:col-span-2" />

            <Input label="Preferred date" type="date" testid="booking-date" value={form.date}
              min={todayStr()} onChange={(v) => set("date", v)} required />

            <div>
              <Lbl>Topic</Lbl>
              <select
                data-testid="booking-topic"
                value={form.topic}
                onChange={(e) => set("topic", e.target.value)}
                className="w-full rounded-lg border border-black/10 bg-white px-4 py-3 text-navy text-sm outline-none focus:border-navy transition-colors"
              >
                {TOPICS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>

            <div className="sm:col-span-2">
              <Lbl>Preferred time</Lbl>
              <div className="grid grid-cols-3 gap-2" data-testid="booking-times">
                {TIMES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`time-${t.replace(/[^0-9]/g, "")}`}
                    onClick={() => set("time", t)}
                    className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${
                      form.time === t ? "bg-navy text-warm-white" : "bg-black/[0.04] text-navy hover:bg-black/[0.08]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="booking-consent" checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-navy" />
              <span className="text-[12px] leading-relaxed text-brand-slate">
                By booking, you authorize a licensed agent to contact you via phone or email regarding insurance options.
              </span>
            </label>

            <button
              type="submit"
              data-testid="booking-submit"
              disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-warm-white font-semibold transition-all hover:bg-slate-navy disabled:opacity-60 active:scale-[0.98]"
            >
              {loading ? <CircleNotch className="animate-spin" size={20} /> : "Confirm consultation"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

const Lbl = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-slate mb-1.5">{children}</label>
);

function Input({ label, value, onChange, type = "text", testid, required, className = "", min }) {
  return (
    <div className={className}>
      <Lbl>{label}</Lbl>
      <input
        type={type} data-testid={testid} value={value} required={required} min={min}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-black/10 bg-white px-4 py-3 text-navy text-sm outline-none focus:border-navy transition-colors placeholder:text-light-slate/60"
      />
    </div>
  );
}
